/**
 * AppLogo - Single source of truth for ModCar app branding
 *
 * This component handles all in-app logo rendering with automatic
 * light/dark theme switching. All logo paths are centralized here.
 *
 * Usage:
 * - Sidebar: <AppLogo variant="icon" size="md" />
 * - Auth screens: <AppLogo variant="full" size="lg" />
 *
 * DO NOT use direct <img> tags with logo paths elsewhere in the app.
 * Always use this component for consistent branding.
 *
 * Exclusions (not handled by this component):
 * - Invoice PDFs (use company branding)
 * - Email templates
 * - Favicon / PWA icons
 * - Marketing website
 */

import { useTheme } from '@/components/theme/ThemeProvider';
import { cn } from '@/lib/utils';

// Centralized logo asset paths - SINGLE SOURCE OF TRUTH
// Available assets in /public:
// - modcar-logo-icon-dark.png (white icon for dark backgrounds)
// - modcar-logo-icon-lightblue.png (purple icon for light backgrounds)
// - modcar-logo-full-dark.png (white wordmark for dark backgrounds)
// - modcar-logo-full-light.png (dark wordmark for light backgrounds)
const LOGO_ASSETS = {
  icon: {
    light: '/modcar-logo-icon-lightblue.png', // Purple icon for light backgrounds
    dark: '/modcar-logo-icon-dark.png', // White icon for dark backgrounds
  },
  full: {
    light: '/modcar-logo-full-light.png', // Dark wordmark for light backgrounds
    dark: '/modcar-logo-full-dark.png', // White wordmark for dark backgrounds
  },
} as const;

// Size presets
const SIZE_PRESETS = {
  sm: { icon: 24, full: 80 },
  md: { icon: 32, full: 120 },
  lg: { icon: 40, full: 160 },
} as const;

export type LogoVariant = 'icon' | 'full';
export type LogoSize = 'sm' | 'md' | 'lg';

export interface AppLogoProps {
  /** Logo variant: icon (square) or full (wordmark) */
  variant: LogoVariant;
  /** Predefined size or custom width in pixels */
  size?: LogoSize | number;
  /** Additional CSS classes */
  className?: string;
  /** Custom alt text (defaults to "ModCar") */
  alt?: string;
}

/**
 * AppLogo component - renders the correct logo based on current theme
 *
 * Automatically switches between light and dark variants based on
 * the resolved theme from ThemeProvider.
 */
export function AppLogo({ variant, size = 'md', className, alt = 'ModCar' }: AppLogoProps) {
  const { resolvedTheme } = useTheme();

  // Get the correct asset path based on theme
  const src = LOGO_ASSETS[variant][resolvedTheme];

  // Calculate dimensions
  const width = typeof size === 'number' ? size : SIZE_PRESETS[size][variant];

  // For icon variant, height equals width (square)
  // For full variant, let height be auto to maintain aspect ratio
  const style: React.CSSProperties =
    variant === 'icon' ? { width, height: width } : { width, height: 'auto' };

  return (
    <img
      src={src}
      alt={alt}
      className={cn('object-contain', className)}
      style={style}
      // Prevent layout shift by reserving space
      loading="eager"
      decoding="sync"
    />
  );
}

/**
 * Get logo asset path directly (for rare cases where component can't be used)
 * Prefer using the AppLogo component instead.
 */
export function getLogoAssetPath(variant: LogoVariant, theme: 'light' | 'dark'): string {
  return LOGO_ASSETS[variant][theme];
}
